import { useMutation } from 'redux-query-react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import {
  SetupIntent,
  CreatePaymentMethodCardData,
  StripeElements,
  ConfirmCardPaymentData,
  ConfirmCardSetupData,
} from '@stripe/stripe-js';
import { useFormikContext } from 'formik';

import { PaymentMethod } from 'EntityTypes';
import { PaymentChargeStatus } from 'constants/payments';
import { fixedSubmitForm } from 'utils/formik';
import { checkSuccess } from 'utils/errors';
import { notify } from 'utils';
import { setupIntentQuery, paymentMethodQuery } from 'modules/payments';
import { RQResponse } from 'types';

import { PaymentMethodFormValues } from './PaymentMethodForm';

/**
 * Returns true if the given payment charge status indicates successful completion (i.e. has been paid)
 */
export function isCompletedChargeStatus(chargeStatus: PaymentChargeStatus): boolean {
  return (
    chargeStatus === PaymentChargeStatus.Completed ||
    chargeStatus === PaymentChargeStatus.CompletedCash
  );
}

/**
 * Creates a Stripe payment method data object to use with Stripe's `confirmCardSetup` and
 * `confirmCardPayment`.
 */
function makeStripePaymentMethodData(
  elements: StripeElements,
  values: PaymentMethodFormValues,
): Pick<CreatePaymentMethodCardData, 'card' | 'billing_details' | 'metadata' | 'payment_method'> {
  return {
    card: elements.getElement('card'),
    billing_details: {
      email: values.email,
      name: values.cardholder_name,
      address: {
        city: values.city,
        country: 'US',
        line1: values.address_line1,
        line2: values.address_line2,
        postal_code: values.zip,
        state: values.state,
      },
    },
  };
}

/**
 * Creates a data object to pass into Stripe's `confirmCardPayment`.
 */
export function makeConfirmCardPaymentData(
  elements: StripeElements,
  values: PaymentMethodFormValues & { save_card: boolean },
): ConfirmCardPaymentData {
  const data: ConfirmCardPaymentData = {
    payment_method: makeStripePaymentMethodData(elements, values),
  };
  if (values.save_card) {
    data.setup_future_usage = 'off_session';
  }
  return data;
}

/**
 * Creates a data object to pass into Stripe's `confirmCardSetup`.
 */
export function makeConfirmCardSetupData(
  elements: StripeElements,
  values: PaymentMethodFormValues,
): ConfirmCardSetupData {
  return {
    payment_method: makeStripePaymentMethodData(elements, values),
  };
}

/**
 * A hook that returns an async function to confirm a card payment with Stripe. The returned function,
 * in turn, returns false if there was an error confirming the payment or true otherwise.
 *
 * The returned function will also show an error message toast if there was an error confirming
 * the payment.
 */
export function useConfirmStripeCardPayment(): (
  clientSecret: string,
  data: ConfirmCardPaymentData,
) => Promise<boolean> {
  const stripe = useStripe();

  const confirmCardPayment = async (
    clientSecret: string,
    data: ConfirmCardPaymentData,
  ): Promise<boolean> => {
    const cardPaymentResponse = await stripe.confirmCardPayment(clientSecret, data);
    if (cardPaymentResponse.error) {
      const isAlreadySucceededError =
        cardPaymentResponse.error.payment_intent?.status === 'succeeded' &&
        cardPaymentResponse.error.code === 'payment_intent_unexpected_state';
      if (!isAlreadySucceededError) {
        if (cardPaymentResponse.error.type === 'card_error') {
          notify(cardPaymentResponse.error.message);
        } else {
          notify('There was an error making the payment. Please try again.');
        }
        return false;
      }
    }

    return true;
  };

  return confirmCardPayment;
}

/**
 * A hook that returns a function to store or update a card for future payments, in both
 * Stripe and Elation.
 *
 * NOTE: This hook requires that the consuming component has Stripe elements and Formik in
 * context.
 *
 * @param patientId - the ID of the patient to associate the card with
 * @param patientKey - the secure key used to authorize access to the Elation public payment method APIs
 * @param onSuccess - success callback
 */
export function useSaveCard(
  patientId: number,
  patientKey: string,
  onSuccess?: (paymentMethod: PaymentMethod) => void,
): () => Promise<unknown> {
  const { setSubmitting, submitForm, validateForm, values } = useFormikContext<
    PaymentMethodFormValues
  >();

  const stripe = useStripe();
  const elements = useElements();

  const [, createSetupIntent] = useMutation(() => setupIntentQuery(patientId, { key: patientKey }));
  const [, createPaymentMethod] = useMutation((setupIntentId: string) =>
    paymentMethodQuery(patientId, { key: patientKey, stripe_setupintent_id: setupIntentId }),
  );

  const saveCard = async (): Promise<unknown> => {
    if (!stripe || !elements) return;

    try {
      // "Submit" form to run validation and touch all inputs. See `handleSubmit` in this
      // component's Formik config for more info on why we do this here.
      await fixedSubmitForm({ submitForm, validateForm });

      try {
        const setupIntentResponse: RQResponse<SetupIntent> = await createSetupIntent();
        checkSuccess(setupIntentResponse);

        const cardSetupResponse = await stripe.confirmCardSetup(
          setupIntentResponse.body.client_secret,
          makeConfirmCardSetupData(elements, values),
        );
        if (cardSetupResponse.error) {
          notify(cardSetupResponse.error.message);
          return;
        }

        const paymentMethodResponse = await createPaymentMethod(cardSetupResponse.setupIntent.id);
        checkSuccess(paymentMethodResponse);

        if (onSuccess) {
          onSuccess(paymentMethodResponse.body);
        }
      } catch (err) {
        notify('There was an error saving the payment method. Please try again.');
      }
    } catch (err) {
      // Validation error. Errors will show inline so we don't need to do anything else
    } finally {
      setSubmitting(false);
    }
  };

  return saveCard;
}
